import { URLS } from 'api/constants';
import { IProvider, ISlotReview } from 'constants/types';
import { useMutation } from 'hooks/useMutation';
import { useQuery } from 'hooks/useQuery';
import { apiGet, apiRequest } from 'lib/api/client';
import { PaginationResponse } from 'lib/api/types';
import { HookQueryOptions, queryKey } from './shared';

interface SlotReviewsRequest {
  casino_id: string;
  slot_id: string;
  page?: number;
  limit?: number;
}

interface SlotAllReviewsRequest {
  casino_id: string;
  isReviewed?: string;
}

interface SlotAllReviewsBySlot {
  slot_id: string;
}

interface CreateSlotReviewRequest {
  casino_id: string;
  slot_id: string;
  value: number;
  image: File;
}

interface SlotReviewsResponse extends PaginationResponse<ISlotReview> {
  averageRating: number;
}

interface SlotAllReviewsResponse extends PaginationResponse<ISlotReview> {
  provider: IProvider;
}

export const useSlotReviewsQuery = (
  payload: SlotReviewsRequest,
  options?: HookQueryOptions
) =>
  useQuery<SlotReviewsResponse>(
    queryKey('slotReviews.list', payload),
    () =>
      apiGet<SlotReviewsResponse>(URLS.SLOT_REVIEWS_BY_CASINO, {
        casino_id: payload.casino_id,
        slot_id: payload.slot_id,
        isReviewed: 'true',
        page: payload.page,
        limit: payload.limit,
      }),
    { skip: options?.skip || !payload?.casino_id || !payload?.slot_id }
  );

export const useSlotReviewsByCasinoQuery = (
  payload: SlotAllReviewsRequest,
  options?: HookQueryOptions
) =>
  useQuery<SlotAllReviewsResponse>(
    queryKey('slotReviews.byCasino', payload),
    () =>
      apiGet<SlotAllReviewsResponse>(URLS.SLOT_REVIEWS_ALL_BY_CASINO, {
        casino_id: payload.casino_id,
        isReviewed: payload.isReviewed ?? 'true',
      }),
    { skip: options?.skip || !payload?.casino_id }
  );

export const useSlotReviewsBySlotQuery = (
  payload: SlotAllReviewsBySlot,
  options?: HookQueryOptions
) =>
  useQuery<SlotAllReviewsResponse>(
    queryKey('slotReviews.bySlot', payload),
    () =>
      apiGet<SlotAllReviewsResponse>(URLS.SLOT_REVIEWS_ALL_BY_SLOT, {
        slot_id: payload.slot_id,
        isReviewed: 'true',
      }),
    { skip: options?.skip || !payload?.slot_id }
  );

export const useCreateSlotReviewMutation = () =>
  useMutation<CreateSlotReviewRequest, void>(
    ({ casino_id, slot_id, value, image }) => {
      const formData = new FormData();
      formData.append('casino_id', casino_id);
      formData.append('slot_id', slot_id);
      formData.append('value', value.toString());
      formData.append('image', image);

      return apiRequest<void>(URLS.SLOT_REVIEWS, {
        method: 'POST',
        auth: true,
        body: formData,
      });
    }
  );

export const useDeleteSlotReviewMutation = () =>
  useMutation<{ id: string }, void>(({ id }) =>
    apiRequest<void>(`${URLS.SLOT_REVIEWS}${id}`, {
      method: 'DELETE',
      auth: true,
    })
  );
